import { getISOWeek, getISOWeekYear } from 'date-fns'
import type { PlannedMeal } from '@/types/meal-plan'
import type { Recipe } from '@/types/recipe'
import type { BasketItem } from '@/types/menu'
import type { MenuAnchorSummary, MenuCandidateSummary } from '@/services/ai-recipe-parser'
import { ingredientAggregator, type MealPlanShoppingListData } from '@/services/ingredients'

export const MENU_SIZE = 6

/**
 * How many dishes of the weekmenu come from the saved collection. The rest are
 * new AI dishes, giving a 1:2 mix of familiar to new so the menu leans on
 * discovery instead of reshuffling the recipe box.
 */
export const MENU_COLLECTION_TARGET = 2

/** A recipe needs at least this rating to anchor newly generated dishes. */
export const ANCHOR_MIN_RATING = 4

/** How many anchor recipes to send along as reference material. */
export const MAX_ANCHORS = 8

/**
 * Splits the weekmenu across three sources, in order of preference: your own
 * recipes (capped at the collection target), the reviewed curated pool, and
 * freshly generated AI dishes for whatever is still missing. A full pool means
 * no AI runs at browse time at all.
 */
export const planMenuComposition = (
  candidateCount: number,
  poolCount = 0,
  size = MENU_SIZE,
  collectionTarget = MENU_COLLECTION_TARGET
): { collectionCount: number; curatedCount: number; ideaCount: number } => {
  const collectionCount = Math.max(0, Math.min(candidateCount, collectionTarget, size))
  const curatedCount = Math.max(0, Math.min(poolCount, size - collectionCount))

  return {
    collectionCount,
    curatedCount,
    ideaCount: Math.max(0, size - collectionCount - curatedCount),
  }
}

/** Reviewed pool dishes: real recipes a person has approved for the menu. */
export const buildPoolCandidates = (recipes: Recipe[]): Recipe[] =>
  buildMenuCandidates(recipes).filter((recipe) => recipe.is_curated)

/** Your own and household recipes, excluding the shared curated pool. */
export const buildOwnCandidates = (
  recipes: Recipe[],
  userId?: string,
  householdId?: string
): Recipe[] =>
  buildMenuCandidates(recipes).filter(
    (recipe) =>
      !recipe.is_curated &&
      (recipe.user_id === userId || (householdId != null && recipe.household_id === householdId))
  )

/** Interleaves saved-recipe and new-dish cards so the grid reads as one menu. */
export const interleaveMenuItems = <T>(collection: T[], ideas: T[]): T[] => {
  const mixed: T[] = []
  const longest = Math.max(collection.length, ideas.length)

  for (let index = 0; index < longest; index += 1) {
    if (index < collection.length) {
      mixed.push(collection[index])
    }
    if (index < ideas.length) {
      mixed.push(ideas[index])
    }
  }

  return mixed
}

export const getCurrentWeekKey = (date = new Date()): string =>
  `${getISOWeekYear(date)}-W${String(getISOWeek(date)).padStart(2, '0')}`

export const formatWeekLabel = (weekKey: string): string => {
  const weekNumber = Number.parseInt(weekKey.split('-W')[1] ?? '', 10)
  return Number.isNaN(weekNumber) ? 'Deze week' : `Week ${weekNumber}`
}

/**
 * Decides whether the weekmenu should (re)generate. Regenerates for a new ISO
 * week or a changed personal/household context, and fills in dishes that are
 * still missing once platform AI becomes available. A menu that the curated
 * pool already completed is never regenerated, and the top-up is skipped when
 * the last attempt errored so a failing AI call can't cause a loop.
 */
export const shouldRegenerateMenu = (params: {
  weekKey: string | null
  storedContextKey: string | null
  currentWeekKey: string
  contextKey: string
  useAI: boolean
  awaitingAI: boolean
  hasItems: boolean
  hasError: boolean
}): boolean => {
  const staleWeekOrContext =
    params.weekKey !== params.currentWeekKey || params.storedContextKey !== params.contextKey
  if (staleWeekOrContext) {
    return true
  }

  return params.useAI && params.awaitingAI && params.hasItems && !params.hasError
}

export const buildMenuCandidates = (recipes: Recipe[]): Recipe[] =>
  recipes.filter(
    (recipe) =>
      recipe.ingredients.length > 0 && (!recipe.meal_type || recipe.meal_type === 'diner')
  )

/**
 * Picks the recipes that newly generated dishes must be anchored to: the
 * highest-rated real recipes first. Falls back to the plain candidate list when
 * nothing is rated yet, so generated dishes are always grounded in actual
 * recipes rather than invented from nothing.
 */
export const selectAnchorRecipes = (recipes: Recipe[], limit = MAX_ANCHORS): Recipe[] => {
  const rated = recipes
    .filter((recipe) => typeof recipe.rating === 'number' && recipe.rating >= ANCHOR_MIN_RATING)
    .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))

  const anchors = rated.length ? rated : recipes

  return anchors.slice(0, limit)
}

export const toAnchorSummaries = (recipes: Recipe[]): MenuAnchorSummary[] =>
  recipes.map((recipe) => ({
    id: recipe.id,
    title: recipe.title,
    rating: recipe.rating,
    tags: recipe.tags.slice(0, 6),
    key_ingredients: recipe.ingredients.slice(0, 8),
  }))

export const toCandidateSummaries = (recipes: Recipe[]): MenuCandidateSummary[] =>
  recipes.map((recipe) => ({
    id: recipe.id,
    title: recipe.title,
    description: recipe.description,
    tags: recipe.tags.slice(0, 6),
    total_time: (recipe.prep_time || 0) + (recipe.cook_time || 0) || undefined,
    difficulty: recipe.difficulty,
  }))

const hashString = (value: string): number => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index)
    hash |= 0
  }
  return hash >>> 0
}

const mulberry32 = (seed: number) => () => {
  seed |= 0
  seed = (seed + 0x6d2b79f5) | 0
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

export const pickWeekRotation = (recipes: Recipe[], count: number, seed: string): Recipe[] => {
  const random = mulberry32(hashString(seed))
  const shuffled = [...recipes]

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    ;[shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]]
  }

  return shuffled.slice(0, count)
}

const toAggregationRecipe = (item: BasketItem, index: number): Recipe => ({
  id: item.recipeId ?? `basket-${index}`,
  title: item.recipe.title,
  ingredients: item.recipe.ingredients,
  instructions: [],
  servings: item.recipe.servings,
  tags: [],
  equipment: [],
  created_at: '',
  updated_at: '',
  user_id: '',
})

export const aggregateBasket = (items: BasketItem[]): MealPlanShoppingListData => {
  const recipes = items.map(toAggregationRecipe)
  const today = new Date().toISOString().split('T')[0]

  const plannedMeals: PlannedMeal[] = items.map((item, index) => ({
    id: `basket-meal-${index}`,
    meal_plan_id: 'weekmenu-basket',
    recipe_id: recipes[index].id,
    planned_date: today,
    meal_type: 'diner',
    planned_servings: item.servings,
    notes: null,
    assigned_to: null,
    created_at: '',
    updated_at: '',
  }))

  return ingredientAggregator.aggregateFromPlannedMeals(plannedMeals, recipes)
}

export const basketToShoppingListText = (items: BasketItem[]): string =>
  ingredientAggregator.exportToFormat(aggregateBasket(items), 'text')
